import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { getAuth, signOut } from "firebase/auth";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { useClassroomMode } from "../shared/classroomMode";
import { isSuperAdmin } from "../shared/superAdmin";
import EmergencyTriggerButton from "../shared/EmergencyTriggerButton";

const link = ({ isActive }) => (isActive ? "nav-link active-link" : "nav-link");

// The shared navigation list, used by both the static desktop sidebar and the
// off-canvas mobile drawer. `onNavigate` lets the drawer close itself when a
// link (or the list) is tapped. The .drawer-only items surface the daily
// classroom actions — which live in the top navbar on desktop — inside the
// mobile drawer, where that top nav is collapsed.
const SidebarNav = ({ onNavigate }) => {
  const { role, uid, user, schoolId, libraryEnabled, lendingTrackers, classroomPin, lunchEnabled, features } = useUser();
  const isAdmin = role === "admin";
  const isElevated = role === "admin" || role === "security";
  const isSuper = isSuperAdmin({ uid, email: user?.email });
  const hasLending = lendingTrackers.length > 0;
  const navigate = useNavigate();
  const { classroomMode, enterClassroomMode } = useClassroomMode();

  // One-click "lock this device into Classroom Mode" from the side nav. If no exit
  // PIN is set yet, send them to set one first (locking with no PIN would trap the
  // device). Confirm before locking — exiting needs the PIN, so an accidental tap
  // mid-class is disruptive. Mirrors the ClassroomModeTip coach-mark's behavior.
  const handleEnterClassroom = () => {
    if (!classroomPin) {
      if (onNavigate) onNavigate();
      navigate("/settings", { state: { tab: "classroom" } });
      return;
    }
    if (!window.confirm(
      "Turn on Classroom Mode on this device? It locks the app to the student-safe " +
      "screens (Sign-Out, Attendance, Library). You'll need your 4-digit PIN to exit."
    )) return;
    enterClassroomMode();
    if (onNavigate) onNavigate();
    navigate("/classsignoutpage");
  };

  const handleLogout = async () => {
    try {
      await signOut(getAuth());
    } catch (e) {
      console.error("Error signing out:", e);
    }
    if (onNavigate) onNavigate(); // close the drawer
    navigate("/");
  };

  // Pending join-request count, so admins get a heads-up badge on Manage Users.
  const [pendingJoins, setPendingJoins] = useState(0);
  useEffect(() => {
    if (!isAdmin || !schoolId) { setPendingJoins(0); return; }
    return onSnapshot(
      query(collection(firestore, "joinRequests"), where("schoolId", "==", schoolId)),
      (snap) => setPendingJoins(snap.docs.filter((d) => d.data().status === "pending").length),
      (err) => console.error("pending join count error:", err),
    );
  }, [isAdmin, schoolId]);

  return (
    <ul className="nav-list" onClick={onNavigate}>
      {features.attendance && (
        <li className="drawer-only"><NavLink to="/attendance"       className={link}>Attendance</NavLink></li>
      )}
      {features.signOut && (
        <li className="drawer-only"><NavLink to="/classsignoutpage" className={link}>Sign Out</NavLink></li>
      )}
      {hasLending && (
        <li className="drawer-only"><NavLink to="/lending"        className={link}>Lending</NavLink></li>
      )}
      {libraryEnabled && (
        <li className="drawer-only"><NavLink to="/library"        className={link}>Library</NavLink></li>
      )}
      {features.checklist && (
        <li className="drawer-only"><NavLink to="/emergency"        className={link}>Check List</NavLink></li>
      )}
      <li className="drawer-only drawer-divider" aria-hidden="true" />

      <li><NavLink to="/dashboard"               className={link}>Dashboard</NavLink></li>
      <li><NavLink to="/displayclasses"          className={link}>Classes</NavLink></li>
      <li><NavLink to="/invitations"             className={link}>Invitations</NavLink></li>
      <li><NavLink to="/displayenrolledstudents" className={link}>Students</NavLink></li>
      <li><NavLink to="/signoutsteacherrecord"   className={link}>Sign-Out History</NavLink></li>
      <li><NavLink to="/studentinsights"         className={link}>Student Insights</NavLink></li>
      <li><NavLink to="/attendanceviewer"        className={link}>Attendance History</NavLink></li>
      {lunchEnabled && (
        <li>
          <NavLink to="/attendance" state={{ mode: "lunch" }} className={link}>
            🍽 Lunch Count
          </NavLink>
        </li>
      )}
      <li><NavLink to="/settings"                className={link}>Settings</NavLink></li>

      {isElevated && (
        <li style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.18)" }}>
          <NavLink to="/schoolview" className={link}>School View</NavLink>
        </li>
      )}

      {isAdmin && (
        <li><NavLink to="/schoolroster" className={link}>School Roster</NavLink></li>
      )}

      {isAdmin && (
        <li>
          <NavLink to="/schooladmin" className={link}>
            Manage Users
            {pendingJoins > 0 && (
              <span
                title={`${pendingJoins} pending join request${pendingJoins > 1 ? "s" : ""}`}
                style={{
                  marginLeft: 8, background: "#dc2626", color: "#fff",
                  borderRadius: 999, fontSize: 11, fontWeight: 700,
                  padding: "1px 7px", minWidth: 18, display: "inline-block", textAlign: "center",
                }}
              >
                {pendingJoins}
              </span>
            )}
          </NavLink>
        </li>
      )}

      {isSuper && (
        <li style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.18)" }}>
          <NavLink to="/adminpanel" className={link}>Super Admin</NavLink>
        </li>
      )}

      {/* One-click Classroom Mode lock, sitting just above Start Emergency (Michael's
          placement). Hidden once already locked (the classroom nav replaces this menu). */}
      {!classroomMode && (
        <li className="nav-classroom-item">
          <button type="button" className="nav-link nav-classroom" onClick={handleEnterClassroom}>
            🔒 Turn on Class Mode
          </button>
        </li>
      )}

      {/* Deliberate, confirm-guarded entry point to START an emergency, sitting just
          above Log Out on the side navbar (desktop sidebar + mobile drawer). Once an
          emergency is live it hides itself — the app-wide banner is the way in then. */}
      <li className="nav-emergency-item">
        <EmergencyTriggerButton className="nav-emergency" onDone={onNavigate} />
      </li>

      {/* Log Out lives here only in the mobile drawer — on desktop the top navbar
          already has a Log Out button, so this would be redundant there. */}
      <li className="drawer-only nav-logout-item">
        <button type="button" className="nav-link nav-logout" onClick={handleLogout}>
          Log Out
        </button>
      </li>
    </ul>
  );
};

export default SidebarNav;
